import { useEffect, useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, Stack, TextField, Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { batchesApi, type StockAvailabilityDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";

/**
 * Corrects the two fields on a batch that get keyed wrong and, until now, could not be changed
 * once stock had been received.
 *
 * <p>Cost price is shown but not editable. It is what inventory is valued at, and moving it after
 * the fact would restate closing stock and cost of goods sold with no journal to explain the
 * change — that correction belongs to a reversal of the receiving document.</p>
 */
export function EditBatchDialog({
  batch, onClose,
}: {
  /** The stock row being corrected; null closes the dialog. */
  batch: StockAvailabilityDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [mrp, setMrp] = useState("0");
  const [expiryDate, setExpiryDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!batch) return;
    setMrp(String(batch.mrp ?? 0));
    setExpiryDate(batch.expiryDate ? batch.expiryDate.slice(0, 10) : "");
    setReason("");
    setError(null);
  }, [batch]);

  const mrpNum = Number(mrp) || 0;
  const belowCost = mrpNum > 0 && batch != null && mrpNum < batch.costPrice;

  const save = useMutation({
    meta: { successMessage: "Batch updated" },
    mutationFn: () => batchesApi.update(batch!.batchId, {
      mrp: mrpNum,
      expiryDate: expiryDate || null,
      reason: reason.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["stock-availability"] });
      qc.invalidateQueries({ queryKey: ["batches"] });
      onClose();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <Dialog open={batch != null} onClose={onClose} maxWidth="sm" fullWidth>
      <ClosableDialogTitle onClose={onClose}>Correct batch</ClosableDialogTitle>
      <DialogContent dividers>
        {batch && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <b>{batch.sku} — {batch.itemName}</b><br />
              Batch {batch.batchNo} · {batch.onHandQty} on hand at {batch.warehouseName}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {batch.mrp <= 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This batch has no MRP, so it bills at the item&apos;s default and the sale ceiling
                is not enforced on it. Enter the price printed on the pack.
              </Alert>
            )}

            <Stack spacing={2}>
              <TextField
                label="MRP (₹)" type="number" size="small" fullWidth
                value={mrp} onChange={(e) => setMrp(e.target.value)}
                error={belowCost}
                helperText={belowCost
                  ? `Below this batch's cost of ₹${batch.costPrice}. Billing is capped at MRP, so it could only be sold at a loss.`
                  : "The price printed on the pack. Zero leaves the batch unpriced."}
                inputProps={{ min: 0, step: "0.01" }}
              />

              <TextField
                label="Expiry" type="date" size="small" fullWidth
                value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Expired stock is refused at dispense with no override, so a wrong date here either strands sellable stock or lets expired stock out."
              />

              <TextField
                label="Reason" size="small" fullWidth multiline minRows={2}
                value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. MRP missed at GRN — taken from vendor invoice INV/2026/4417"
                helperText="Recorded on the audit entry against your name."
                inputProps={{ maxLength: 500 }}
              />

              <TextField
                label="Cost price (₹)" size="small" fullWidth disabled
                value={batch.costPrice}
                helperText="Set by the receiving document. Correct it by reversing that document, so the accounting follows."
              />
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained"
          disabled={belowCost || save.isPending}
          onClick={() => save.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
